import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  Clock,
  Compass,
  ExternalLink,
  Minus,
  Plus,
  Search,
  TrendingDown,
  TrendingUp,
  Trash2,
  X,
} from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { DropdownField, FormField, MoneyField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  generateId,
  useApp,
  type CardCondition,
  type PriceEntry,
  type PriceSource,
} from "@/context/AppContext";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/tools/pricetrack")({
  component: () => (
    <ManagerOnly>
      <PriceTrackPage />
    </ManagerOnly>
  ),
});

// ─── Constants ──────────────────────────────────────────────────────────────

interface TcgCard {
  id: string;
  name: string;
  number: string;
  set: { id: string; name: string };
  tcgplayer?: {
    prices?: Record<string, { market?: number; low?: number; high?: number }>;
  };
}

const PRICE_TYPE_LABELS: Record<string, string> = {
  normal: "Normal",
  holofoil: "Holo",
  reverseHolofoil: "Rev. Holo",
  "1stEditionHolofoil": "1st Ed. Holo",
  "1stEditionNormal": "1st Ed. Normal",
  unlimitedHolofoil: "Unlimited Holo",
};

const CONDITIONS: CardCondition[] = ["NM", "LP", "MP", "HP", "Damaged"];
const SOURCES: PriceSource[] = ["eBay Sold", "TCGplayer", "Manual", "Other"];

const CONDITION_FACTORS: Record<CardCondition, number> = {
  NM: 1.0,
  LP: 0.8,
  MP: 0.65,
  HP: 0.45,
  Damaged: 0.25,
};

const PLATFORM_OPTIONS = [
  "eBay",
  "TCGplayer",
  "Facebook Marketplace",
  "Local",
  "Custom",
];
// NOTE (TNK-48/46): fees → shared Settings "default fees" source; consider
// surfacing this tracker as a per-card panel on /inventory/$id.
// docs/settings-audit.md + docs/tools-organization-review.md.
const PLATFORM_FEES: Record<string, number> = {
  eBay: 13.25,
  TCGplayer: 10.25,
  "Facebook Marketplace": 5.0,
  Local: 0,
  Custom: 0,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function escapeLucene(term: string): string {
  return term.replace(/[+\-!(){}[\]^"~?:\\/]/g, "\\$&");
}

const TCG_TIMEOUT_MS = 30_000;
const TCG_CACHE_TTL_MS = 5 * 60_000;

// In-session cache keyed by full URL. Repeated queries (tab-switching, retries,
// "Charizard" looked up twice) return instantly without re-hitting the API.
// Entries auto-expire after 5 minutes so prices don't go stale across longer
// sessions.
const tcgCache = new Map<string, { at: number; cards: TcgCard[] }>();

async function queryTcgApi(
  luceneQuery: string,
  signal?: AbortSignal,
  pageSize = 15,
): Promise<TcgCard[]> {
  const url = `https://api.pokemontcg.io/v2/cards?q=${encodeURIComponent(
    luceneQuery,
  )}&select=id,name,set,number,tcgplayer&pageSize=${pageSize}`;

  const cached = tcgCache.get(url);
  if (cached && Date.now() - cached.at < TCG_CACHE_TTL_MS) {
    return cached.cards;
  }

  // Race the user-supplied signal (if any) against a hard timeout so a stalled
  // pokemontcg.io response can't leave the UI spinning forever.
  const timeoutCtrl = new AbortController();
  const timer = setTimeout(() => timeoutCtrl.abort(), TCG_TIMEOUT_MS);
  const onUserAbort = () => timeoutCtrl.abort();
  signal?.addEventListener("abort", onUserAbort);

  try {
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      signal: timeoutCtrl.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = (await res.json()) as { data?: TcgCard[] };
    const cards = Array.isArray(json.data) ? json.data : [];
    tcgCache.set(url, { at: Date.now(), cards });
    return cards;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onUserAbort);
  }
}

function buildEbaySoldUrl(
  cardName: string,
  setName: string,
  condition?: CardCondition,
): string {
  const parts = [cardName, setName, "pokemon card"];
  if (condition && condition !== "NM") parts.push(condition);
  const query = encodeURIComponent(parts.join(" "));
  return `https://www.ebay.com/sch/i.html?_nkw=${query}&LH_Sold=1&LH_Complete=1`;
}

function getBestMarketPrice(
  card: TcgCard,
): { type: string; price: number } | null {
  const prices = card.tcgplayer?.prices ?? {};
  const priority = [
    "holofoil",
    "reverseHolofoil",
    "normal",
    "1stEditionHolofoil",
    "unlimitedHolofoil",
    "1stEditionNormal",
  ];
  for (const type of priority) {
    if (prices[type]?.market != null)
      return { type, price: prices[type].market! };
  }
  const first = Object.entries(prices).find(([, p]) => p.market != null);
  return first ? { type: first[0], price: first[1].market! } : null;
}

// ─── Page ───────────────────────────────────────────────────────────────────

interface LogFormState {
  cardName: string;
  setName: string;
  setCode: string;
  cardNumber: string;
  price: string;
  source: PriceSource;
  condition: CardCondition;
  notes: string;
}

interface GuideState {
  cardName: string;
  setName: string;
  marketPrice: number | null;
  marketType: string;
  condition: CardCondition;
  platform: string;
  customFee: string;
  targetMargin: string;
  costBasis: string;
  loading: boolean;
  loaded: boolean;
  error: string;
}

function PriceTrackPage() {
  const { priceEntries, addPriceEntry, deletePriceEntry } = useApp();

  const [activeTab, setActiveTab] = useState<"search" | "history" | "guide">(
    "search",
  );
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Search tab
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TcgCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchErr, setSearchErr] = useState("");

  const searchAbort = useRef<AbortController | null>(null);

  const search = async () => {
    const raw = query.trim();
    if (!raw) return;
    searchAbort.current?.abort();
    const ctrl = new AbortController();
    searchAbort.current = ctrl;
    setLoading(true);
    setSearchErr("");
    setResults([]);
    try {
      const words = raw.split(/\s+/);
      let cards: TcgCard[] = [];

      if (words.length === 1) {
        const safe = escapeLucene(raw);
        cards = await queryTcgApi(`name:*${safe}*`, ctrl.signal);
      } else {
        const phrase = raw.replace(/"/g, "");
        cards = await queryTcgApi(`name:"${phrase}"`, ctrl.signal);
        if (cards.length === 0) {
          const first = escapeLucene(words[0].replace(/[.,']$/g, ""));
          if (first.length >= 2)
            cards = await queryTcgApi(`name:*${first}*`, ctrl.signal);
        }
      }

      if (cards.length > 0) {
        setResults(cards);
      } else {
        setSearchErr(
          words.length > 1
            ? `No cards found for "${raw}". Try just the first part of the name, e.g. "${words[0]}".`
            : `No cards found for "${raw}". Check spelling or try a partial name.`,
        );
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setSearchErr(
          "TCG API timed out after 15 seconds. Try again or check your connection.",
        );
      } else {
        setSearchErr("Could not reach Pokémon TCG API. Check your connection.");
      }
    } finally {
      setLoading(false);
      if (searchAbort.current === ctrl) searchAbort.current = null;
    }
  };

  // Log form
  const [logForm, setLogForm] = useState<LogFormState | null>(null);
  const [logFormErrors, setLogFormErrors] = useState({
    cardName: "",
    price: "",
    source: "",
    condition: "",
  });

  const openLogForm = (card: TcgCard, priceType: string, price: number) => {
    setLogFormErrors({ cardName: "", price: "", source: "", condition: "" });
    setLogForm({
      cardName: card.name,
      setName: card.set.name,
      setCode: card.set.id,
      cardNumber: card.number,
      price: price.toFixed(2),
      source: "TCGplayer",
      condition: "NM",
      notes: PRICE_TYPE_LABELS[priceType]
        ? `${PRICE_TYPE_LABELS[priceType]} market`
        : "",
    });
    setActiveTab("history");
  };

  const openManualLogForm = () => {
    setLogFormErrors({ cardName: "", price: "", source: "", condition: "" });
    setLogForm({
      cardName: "",
      setName: "",
      setCode: "",
      cardNumber: "",
      price: "",
      source: "eBay Sold",
      condition: "NM",
      notes: "",
    });
  };

  const saveLog = () => {
    if (!logForm) return;
    const price = parseFloat(logForm.price);
    const errors = { cardName: "", price: "", source: "", condition: "" };
    if (!logForm.cardName.trim()) errors.cardName = "Enter a card name";
    if (isNaN(price) || price <= 0) errors.price = "Enter a valid price";
    if (!logForm.source) errors.source = "Select a source";
    if (!logForm.condition) errors.condition = "Select a condition";
    if (errors.cardName || errors.price || errors.source || errors.condition) {
      setLogFormErrors(errors);
      return;
    }
    setLogFormErrors({ cardName: "", price: "", source: "", condition: "" });
    addPriceEntry({
      id: generateId("PE"),
      cardName: logForm.cardName.trim(),
      setName: logForm.setName.trim(),
      setCode: logForm.setCode.trim(),
      cardNumber: logForm.cardNumber.trim(),
      condition: logForm.condition,
      price,
      source: logForm.source,
      notes: logForm.notes.trim(),
      loggedAt: new Date().toISOString(),
    });
    setLogForm(null);
  };

  const confirmDelete = (id: string) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    deletePriceEntry(id);
    setConfirmDeleteId(null);
  };

  // History grouping
  const groupedHistory = useMemo(() => {
    const sorted = [...priceEntries].sort(
      (a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime(),
    );
    const groups: Record<string, PriceEntry[]> = {};
    for (const e of sorted) {
      const key = `${e.cardName.toLowerCase()}|${e.setName}|${e.condition}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(e);
    }
    return Object.values(groups).sort(
      (a, b) =>
        new Date(b[0].loggedAt).getTime() - new Date(a[0].loggedAt).getTime(),
    );
  }, [priceEntries]);

  const getPriceTrend = (entries: PriceEntry[]): number | null => {
    if (entries.length < 2) return null;
    const latest = entries[0].price;
    const prev = entries[1].price;
    return prev > 0 ? ((latest - prev) / prev) * 100 : null;
  };

  // Price guide
  const [guide, setGuide] = useState<GuideState>({
    cardName: "",
    setName: "",
    marketPrice: null,
    marketType: "",
    condition: "NM",
    platform: "eBay",
    customFee: "13.25",
    targetMargin: "20",
    costBasis: "",
    loading: false,
    loaded: false,
    error: "",
  });
  const [guideQuery, setGuideQuery] = useState("");
  const guideAbort = useRef<AbortController | null>(null);

  const fillGuideFromCard = (card: TcgCard) => {
    const best = getBestMarketPrice(card);
    setGuide((g) => ({
      ...g,
      cardName: card.name,
      setName: card.set.name,
      marketPrice: best?.price ?? null,
      marketType: best?.type ?? "",
      loaded: true,
      loading: false,
      error: "",
    }));
    setGuideQuery(card.name);
    setActiveTab("guide");
  };

  const fetchGuideMarket = async () => {
    const raw = guideQuery.trim();
    if (!raw) return;
    // Cancel any in-flight fetch so the latest click always wins.
    guideAbort.current?.abort();
    const ctrl = new AbortController();
    guideAbort.current = ctrl;
    setGuide((g) => ({
      ...g,
      loading: true,
      loaded: false,
      error: "",
      marketPrice: null,
    }));
    try {
      const words = raw.split(/\s+/);
      let cards: TcgCard[] = [];
      // Guide tab only ever reads cards[0]; pageSize=1 makes pokemontcg.io
      // return ~10× faster than the default 15-card payload.
      if (words.length === 1) {
        cards = await queryTcgApi(
          `name:*${escapeLucene(raw)}*`,
          ctrl.signal,
          1,
        );
      } else {
        cards = await queryTcgApi(
          `name:"${raw.replace(/"/g, "")}"`,
          ctrl.signal,
          1,
        );
        if (cards.length === 0) {
          cards = await queryTcgApi(
            `name:*${escapeLucene(words[0])}*`,
            ctrl.signal,
            1,
          );
        }
      }
      const best = cards.length > 0 ? getBestMarketPrice(cards[0]) : null;
      setGuide((g) => ({
        ...g,
        cardName: cards[0]?.name ?? raw,
        setName: cards[0]?.set.name ?? "",
        marketPrice: best?.price ?? null,
        marketType: best?.type ?? "",
        loading: false,
        loaded: true,
        error: "",
      }));
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        // Either user-cancelled (new click) or hit the 15s timeout.
        setGuide((g) => ({
          ...g,
          loading: false,
          loaded: false,
          error:
            "TCG API timed out after 15 seconds. Try again or check your connection.",
        }));
        return;
      }
      setGuide((g) => ({
        ...g,
        loading: false,
        loaded: false,
        error: "Could not reach Pokémon TCG API. Check your connection.",
      }));
    } finally {
      if (guideAbort.current === ctrl) guideAbort.current = null;
    }
  };

  const guidePlatformFeeRate =
    guide.platform === "Custom"
      ? parseFloat(guide.customFee) || 0
      : (PLATFORM_FEES[guide.platform] ?? 0);

  const guideCalc = useMemo(() => {
    const condFactor = CONDITION_FACTORS[guide.condition];
    const totalFeeRate = (guidePlatformFeeRate + 2.9) / 100;
    const marginRate = (parseFloat(guide.targetMargin) || 0) / 100;

    const nameLower = guide.cardName.toLowerCase();
    const comps = priceEntries.filter(
      (e) =>
        e.cardName.toLowerCase() === nameLower &&
        e.condition === guide.condition,
    );
    const allComps = priceEntries.filter(
      (e) => e.cardName.toLowerCase() === nameLower,
    );

    const compPrices = comps.map((e) => e.price);
    const compAvg =
      compPrices.length > 0
        ? compPrices.reduce((a, b) => a + b, 0) / compPrices.length
        : null;
    const compMin = compPrices.length > 0 ? Math.min(...compPrices) : null;
    const compMax = compPrices.length > 0 ? Math.max(...compPrices) : null;

    const conditionedMarket =
      guide.marketPrice != null ? guide.marketPrice * condFactor : null;

    let basePrice: number | null = null;
    if (compAvg != null && conditionedMarket != null) {
      basePrice = compAvg * 0.6 + conditionedMarket * 0.4;
    } else if (compAvg != null) {
      basePrice = compAvg;
    } else if (conditionedMarket != null) {
      basePrice = conditionedMarket;
    }

    if (basePrice == null || basePrice <= 0) {
      return {
        comps,
        allComps,
        compAvg,
        compMin,
        compMax,
        conditionedMarket,
        basePrice: null,
        suggestedPrice: null,
        minPrice: null,
        netAfterFees: null,
        roi: null,
        totalFeeRate,
      };
    }

    const divisor = 1 - totalFeeRate - marginRate;
    const suggestedPrice = divisor > 0 ? basePrice / divisor : null;
    const minPrice =
      conditionedMarket != null ? conditionedMarket / (1 - totalFeeRate) : null;

    const netAfterFees =
      suggestedPrice != null ? suggestedPrice * (1 - totalFeeRate) : null;
    const cost = parseFloat(guide.costBasis) || null;
    const roi =
      cost != null && netAfterFees != null && cost > 0
        ? ((netAfterFees - cost) / cost) * 100
        : null;

    return {
      comps,
      allComps,
      compAvg,
      compMin,
      compMax,
      conditionedMarket,
      basePrice,
      suggestedPrice,
      minPrice,
      netAfterFees,
      roi,
      totalFeeRate,
    };
  }, [guide, priceEntries, guidePlatformFeeRate]);

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Price Tracker</h1>
        <p className="text-sm text-muted-foreground">
          Search live market prices, log your own comps, and get a suggested
          listing price.
        </p>
      </div>

      <div
        className="flex-1 space-y-4 overflow-y-auto py-4 pr-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {/* Tab bar */}
        <div className="flex gap-1 rounded-md bg-card p-1">
          {(
            [
              ["search", "Search"],
              ["history", `Log (${priceEntries.length})`],
              ["guide", "Price Guide"],
            ] as const
          ).map(([t, label]) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTab(t)}
              className={`flex-1 rounded-md py-2 text-center text-xs font-semibold ${
                activeTab === t
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* SEARCH TAB */}
        {activeTab === "search" && (
          <>
            <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void search();
                }}
                placeholder="Charizard, Mr. Mime, Ho-Oh…"
                className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
              {query.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setResults([]);
                    setSearchErr("");
                  }}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-start gap-2 rounded-md bg-card px-3 py-2 text-xs text-muted-foreground">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Multi-word names (Mr. Mime, Ho-Oh, Farfetch&apos;d) are searched
              as exact phrases automatically.
            </div>

            <PrimaryButton
              label={loading ? "Searching…" : "Search TCG API"}
              loading={loading}
              onClick={search}
              className="w-full"
            />

            {searchErr && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {searchErr}
              </div>
            )}

            {results.map((card) => {
              const prices = card.tcgplayer?.prices ?? {};
              const priceTypes = Object.entries(prices).filter(
                ([, p]) => p.market != null,
              );
              return (
                <div
                  key={card.id}
                  className="space-y-3 rounded-md border border-border bg-card p-3"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground">
                        {card.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {card.set.name} · #{card.number}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <a
                        href={buildEbaySoldUrl(card.name, card.set.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded border border-secondary px-2 py-0.5 text-[11px] font-semibold text-secondary hover:bg-secondary/10"
                      >
                        <ExternalLink className="h-3 w-3" />
                        eBay Sold
                      </a>
                      <button
                        type="button"
                        onClick={() => fillGuideFromCard(card)}
                        className="inline-flex items-center gap-1 rounded border border-primary bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/25"
                      >
                        <Compass className="h-3 w-3" />
                        Price Guide
                      </button>
                    </div>
                  </div>

                  {priceTypes.length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                      No pricing data available.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {priceTypes.map(([type, p]) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => openLogForm(card, type, p.market!)}
                          className="space-y-0.5 rounded-md bg-muted px-3 py-2 text-left hover:bg-muted/70"
                        >
                          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            {PRICE_TYPE_LABELS[type] ?? type}
                          </div>
                          <div className="text-lg font-bold text-secondary">
                            ${p.market!.toFixed(2)}
                          </div>
                          {p.low != null && p.high != null && (
                            <div className="text-[10px] text-muted-foreground">
                              ${p.low.toFixed(2)}–${p.high.toFixed(2)}
                            </div>
                          )}
                          <div className="mt-1 inline-block rounded bg-primary/20 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                            + Log
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {results.length === 0 && !loading && !searchErr && (
              <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-10 text-center">
                <Search className="h-7 w-7 text-muted-foreground" />
                <div className="text-sm font-semibold text-foreground">
                  Search any card name
                </div>
                <div className="text-xs leading-relaxed text-muted-foreground">
                  Multi-word and special names work:
                  <br />
                  Mr. Mime · Ho-Oh · Farfetch&apos;d · Jynx
                </div>
              </div>
            )}
          </>
        )}

        {/* LOG TAB */}
        {activeTab === "history" && (
          <>
            {!logForm && (
              <PrimaryButton
                label="Log Price Manually"
                leadingIcon={<Plus className="h-4 w-4" />}
                onClick={openManualLogForm}
                className="w-full"
              />
            )}

            {logForm && (
              <div className="space-y-2 rounded-md border border-secondary/40 bg-card p-4">
                <div className="text-base font-bold text-foreground">
                  Log Price Entry
                </div>
                <FormField
                  label="Card Name"
                  required
                  value={logForm.cardName}
                  onChangeText={(v) => {
                    setLogForm({ ...logForm, cardName: v });
                    if (v.trim())
                      setLogFormErrors((prev) => ({ ...prev, cardName: "" }));
                  }}
                  placeholder="e.g. Charizard"
                  error={logFormErrors.cardName}
                />
                <FormField
                  label="Set Name"
                  value={logForm.setName}
                  onChangeText={(v) => setLogForm({ ...logForm, setName: v })}
                  placeholder="e.g. Vivid Voltage"
                />
                <FormField
                  label="Card # (optional)"
                  value={logForm.cardNumber}
                  onChangeText={(v) =>
                    setLogForm({ ...logForm, cardNumber: v })
                  }
                  placeholder="e.g. 25/185"
                />
                <MoneyField
                  label="Price"
                  required
                  value={logForm.price}
                  onChangeText={(v) => {
                    setLogForm({ ...logForm, price: v });
                    if (v.trim())
                      setLogFormErrors((prev) => ({ ...prev, price: "" }));
                  }}
                  error={logFormErrors.price}
                />
                <DropdownField
                  label="Source"
                  required
                  value={logForm.source}
                  options={SOURCES}
                  onChange={(v) => {
                    setLogForm({ ...logForm, source: v as PriceSource });
                    setLogFormErrors((prev) => ({ ...prev, source: "" }));
                  }}
                  error={logFormErrors.source}
                />
                <DropdownField
                  label="Condition"
                  required
                  value={logForm.condition}
                  options={CONDITIONS}
                  onChange={(v) => {
                    setLogForm({
                      ...logForm,
                      condition: v as CardCondition,
                    });
                    setLogFormErrors((prev) => ({ ...prev, condition: "" }));
                  }}
                  error={logFormErrors.condition}
                />
                <FormField
                  label="Notes"
                  value={logForm.notes}
                  onChangeText={(v) => setLogForm({ ...logForm, notes: v })}
                  placeholder="e.g. Holo foil, BGS slab"
                  multiline
                />
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setLogForm(null);
                      setLogFormErrors({
                        cardName: "",
                        price: "",
                        source: "",
                        condition: "",
                      });
                    }}
                    className="flex-1 rounded-md border border-border bg-card py-2 text-sm font-semibold text-muted-foreground hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveLog}
                    className="flex-1 rounded-md bg-secondary py-2 text-sm font-bold text-primary hover:opacity-90"
                  >
                    Save Entry
                  </button>
                </div>
              </div>
            )}

            {groupedHistory.length === 0 && !logForm ? (
              <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-10 text-center">
                <Clock className="h-7 w-7 text-muted-foreground" />
                <div className="text-sm font-semibold text-foreground">
                  No prices logged yet
                </div>
                <div className="text-xs leading-relaxed text-muted-foreground">
                  Search for a card and tap a price chip to log it, or add
                  manually above.
                </div>
              </div>
            ) : (
              groupedHistory.map((entries) => {
                const latest = entries[0];
                const trendPct = getPriceTrend(entries);
                return (
                  <div
                    key={latest.id}
                    className="overflow-hidden rounded-md border border-border bg-card"
                  >
                    <div className="flex items-start gap-2 p-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-foreground">
                          {latest.cardName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {latest.setName}
                          {latest.cardNumber
                            ? ` · #${latest.cardNumber}`
                            : ""}{" "}
                          · {latest.condition}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        <div className="text-lg font-bold text-secondary">
                          ${latest.price.toFixed(2)}
                        </div>
                        {trendPct !== null && <TrendChip pct={trendPct} />}
                      </div>
                    </div>

                    {entries.map((e, i) => (
                      <div
                        key={e.id}
                        className={`flex items-center justify-between gap-2 px-3 py-2 ${
                          i === 0
                            ? "border-t border-border"
                            : "border-t border-border/60"
                        }`}
                      >
                        <div className="min-w-0 flex-1 space-y-0.5">
                          <div className="text-[11px] text-muted-foreground">
                            {new Date(e.loggedAt).toLocaleDateString()}
                          </div>
                          <div className="inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {e.source}
                          </div>
                          {e.notes && (
                            <div className="truncate text-[11px] text-muted-foreground">
                              {e.notes}
                            </div>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <div
                            className={`text-sm font-semibold ${i === 0 ? "text-foreground" : "text-muted-foreground"}`}
                          >
                            ${e.price.toFixed(2)}
                          </div>
                          {confirmDeleteId === e.id ? (
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(null)}
                                className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground hover:bg-muted/70"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => confirmDelete(e.id)}
                                className="rounded-md bg-destructive px-2 py-0.5 text-[11px] font-semibold text-white hover:opacity-90"
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => confirmDelete(e.id)}
                              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    <a
                      href={buildEbaySoldUrl(
                        latest.cardName,
                        latest.setName,
                        latest.condition,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 border-t border-border py-2.5 text-xs font-semibold text-secondary hover:bg-muted/40"
                    >
                      <ExternalLink className="h-3 w-3" />
                      View eBay Sold Listings
                    </a>
                  </div>
                );
              })
            )}
          </>
        )}

        {/* PRICE GUIDE TAB */}
        {activeTab === "guide" && (
          <>
            {/* Card lookup */}
            <Section title="Card">
              <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  type="text"
                  value={guideQuery}
                  onChange={(e) => setGuideQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void fetchGuideMarket();
                  }}
                  placeholder="Enter card name…"
                  className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                />
              </div>
              <button
                type="button"
                onClick={fetchGuideMarket}
                disabled={guide.loading}
                className="mt-2 w-full rounded-md bg-primary py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {guide.loading ? "Fetching…" : "Fetch Market Price"}
              </button>

              {guide.error && (
                <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {guide.error}
                </div>
              )}

              {guide.loaded && !guide.error && (
                <div className="mt-3 space-y-1 rounded-md bg-primary/15 p-3">
                  {guide.marketPrice != null ? (
                    <>
                      <div className="text-sm font-semibold text-foreground">
                        {guide.cardName}
                      </div>
                      {guide.setName && (
                        <div className="text-xs text-muted-foreground">
                          {guide.setName}
                        </div>
                      )}
                      <div className="pt-1 text-[11px] text-muted-foreground">
                        TCGplayer Market (
                        {PRICE_TYPE_LABELS[guide.marketType] ??
                          guide.marketType}
                        ) — NM
                      </div>
                      <div className="text-2xl font-bold text-secondary">
                        ${guide.marketPrice.toFixed(2)}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No market price found for &quot;{guideQuery}&quot;. You
                      can still use your logged comps below.
                    </div>
                  )}
                </div>
              )}
            </Section>

            {/* Comps */}
            <Section title="Your Comps">
              {guideCalc.comps.length > 0 ? (
                <>
                  <div className="flex items-stretch divide-x divide-border">
                    {[
                      ["Comps", String(guideCalc.comps.length)],
                      [
                        "Avg",
                        guideCalc.compAvg != null
                          ? `$${guideCalc.compAvg.toFixed(2)}`
                          : "—",
                      ],
                      [
                        "Low",
                        guideCalc.compMin != null
                          ? `$${guideCalc.compMin.toFixed(2)}`
                          : "—",
                      ],
                      [
                        "High",
                        guideCalc.compMax != null
                          ? `$${guideCalc.compMax.toFixed(2)}`
                          : "—",
                      ],
                    ].map(([label, val]) => (
                      <div
                        key={label}
                        className="flex flex-1 flex-col items-center gap-0.5 px-2"
                      >
                        <div className="text-sm font-bold text-foreground">
                          {val}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-0 pt-2">
                    {guideCalc.comps.slice(0, 5).map((e) => (
                      <div
                        key={e.id}
                        className="flex items-center justify-between gap-2 border-b border-border py-2 last:border-0"
                      >
                        <div>
                          <div className="text-[11px] text-muted-foreground">
                            {new Date(e.loggedAt).toLocaleDateString()}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {e.source}
                            {e.notes ? ` · ${e.notes}` : ""}
                          </div>
                        </div>
                        <div className="text-sm font-semibold text-foreground">
                          ${e.price.toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">
                    {guide.cardName
                      ? `No ${guide.condition} comps logged for "${guide.cardName}".`
                      : "Search for a card above to see your logged comps."}
                  </div>
                  {guideCalc.allComps.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      You have {guideCalc.allComps.length} comp
                      {guideCalc.allComps.length > 1 ? "s" : ""} logged for
                      other conditions.
                    </div>
                  )}
                </div>
              )}
            </Section>

            {/* Settings */}
            <Section title="Settings">
              <DropdownField
                label="Condition"
                value={guide.condition}
                options={CONDITIONS}
                onChange={(v) =>
                  setGuide({ ...guide, condition: v as CardCondition })
                }
              />
              <DropdownField
                label="Selling Platform"
                value={guide.platform}
                options={PLATFORM_OPTIONS}
                onChange={(v) => setGuide({ ...guide, platform: v })}
              />
              {guide.platform === "Custom" && (
                <FormField
                  label="Custom Fee %"
                  value={guide.customFee}
                  onChangeText={(v) => setGuide({ ...guide, customFee: v })}
                  inputMode="decimal"
                  placeholder="13.25"
                  suffix="%"
                />
              )}
              <FormField
                label="Target Net Margin %"
                value={guide.targetMargin}
                onChangeText={(v) => setGuide({ ...guide, targetMargin: v })}
                inputMode="decimal"
                placeholder="20"
                suffix="%"
              />
              <MoneyField
                label="Your Cost Basis (optional, for ROI)"
                value={guide.costBasis}
                onChangeText={(v) => setGuide({ ...guide, costBasis: v })}
              />
            </Section>

            {/* Results */}
            {guideCalc.suggestedPrice != null ? (
              <div className="space-y-3 rounded-md bg-primary p-4">
                <div className="text-[11px] font-medium uppercase tracking-wide text-primary-foreground/60">
                  Suggested Listing Price
                </div>

                <div className="flex flex-col items-center gap-1 py-2">
                  <div className="text-5xl font-bold leading-none text-secondary">
                    ${guideCalc.suggestedPrice.toFixed(2)}
                  </div>
                  <div className="text-sm text-primary-foreground/70">
                    list price on {guide.platform}
                  </div>
                </div>

                <div className="space-y-0">
                  {[
                    [
                      "Min. Break-Even Price",
                      guideCalc.minPrice != null
                        ? `$${guideCalc.minPrice.toFixed(2)}`
                        : "—",
                    ],
                    [
                      "Condition Adjustment",
                      `${(CONDITION_FACTORS[guide.condition] * 100).toFixed(0)}% (${guide.condition})`,
                    ],
                    [
                      "Platform Fees",
                      `${guidePlatformFeeRate.toFixed(2)}% + 2.9% payment`,
                    ],
                    [
                      "Net After All Fees",
                      guideCalc.netAfterFees != null
                        ? `$${guideCalc.netAfterFees.toFixed(2)}`
                        : "—",
                    ],
                    ...(guideCalc.roi != null
                      ? [["ROI on Cost Basis", `${guideCalc.roi.toFixed(1)}%`]]
                      : []),
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="flex items-center justify-between border-b border-white/10 py-2 last:border-0"
                    >
                      <div className="text-sm text-primary-foreground/70">
                        {label}
                      </div>
                      <div className="text-sm font-semibold text-primary-foreground">
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 rounded-md bg-white/5 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-primary-foreground/50">
                    How we calculated this
                  </div>
                  <div className="space-y-1 text-xs leading-relaxed text-primary-foreground/80">
                    <div>
                      {guideCalc.compAvg != null &&
                      guideCalc.conditionedMarket != null
                        ? `Base = 60% of your avg comp ($${guideCalc.compAvg.toFixed(2)}) + 40% of condition-adjusted market ($${guideCalc.conditionedMarket.toFixed(2)}) = $${guideCalc.basePrice!.toFixed(2)}`
                        : guideCalc.compAvg != null
                          ? `Base = avg of your ${guideCalc.comps.length} logged comp${guideCalc.comps.length > 1 ? "s" : ""} ($${guideCalc.compAvg.toFixed(2)})`
                          : `Base = TCGplayer market × ${(CONDITION_FACTORS[guide.condition] * 100).toFixed(0)}% condition factor = $${guideCalc.conditionedMarket!.toFixed(2)}`}
                    </div>
                    <div>
                      Fee markup: base ÷ (1 −{" "}
                      {(guidePlatformFeeRate + 2.9).toFixed(1)}% fees −{" "}
                      {guide.targetMargin}% margin)
                    </div>
                  </div>
                </div>

                <a
                  href={buildEbaySoldUrl(
                    guide.cardName,
                    guide.setName,
                    guide.condition,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 border-t border-white/15 pt-3 text-sm font-semibold text-secondary hover:opacity-90"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Verify with eBay Sold Listings
                </a>
              </div>
            ) : guide.loaded || guideCalc.comps.length > 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-8 text-center">
                <AlertCircle className="h-6 w-6 text-muted-foreground" />
                <div className="text-sm font-semibold text-foreground">
                  Need at least one data point
                </div>
                <div className="text-xs text-muted-foreground">
                  Fetch a market price above, or log at least one sold comp for
                  this card.
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-10 text-center">
                <Compass className="h-7 w-7 text-muted-foreground" />
                <div className="text-sm font-semibold text-foreground">
                  Suggested Listing Price
                </div>
                <div className="text-xs leading-relaxed text-muted-foreground">
                  Search a card and tap &quot;Price Guide&quot; on a result, or
                  type the name above and fetch market price.
                  <br />
                  <br />
                  Uses your logged comps + TCGplayer market to calculate a smart
                  list price after fees.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1 rounded-md border border-border bg-card p-4">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function TrendChip({ pct }: { pct: number }) {
  const Icon = pct > 2 ? TrendingUp : pct < -2 ? TrendingDown : Minus;
  const color =
    pct > 2
      ? "text-emerald-500"
      : pct < -2
        ? "text-rose-500"
        : "text-muted-foreground";
  return (
    <div className={`flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      <span>
        {pct > 0 ? "+" : ""}
        {pct.toFixed(1)}%
      </span>
    </div>
  );
}
